import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Text, Heading, Box, Container } from 'roo-ui/components';
import {
  QHPackagesPageLink,
  LiveChatLink,
  ContactUsLink,
  TermsAndConditionsLink,
  QHHomePageLink,
  QHSupportLink,
  SelfServiceLink,
} from './Links';
import FaqNavBar from './FaqNavBar';
import {
  Wrapper,
  TitleText,
  QuestionWrapper,
  QuestionText,
  AnswerWrapper,
  AnswerText,
  Table,
  Column,
  TableCell,
  SubTitleText,
  Topic,
} from './primitives';

const FAQsLayout = () => {
  return (
    <Wrapper>
      <Helmet title="FAQs | Qantas Hotels Australia" htmlAttributes={{ itemscope: true, itemtype: 'https://schema.org/FAQPage' }} />
      <Box bg="white">
        <Container py={[4, 8]}>
          <Heading.h1 mt={11}>Frequently Asked Questions</Heading.h1>
          <Text mt={5} fontSize="md">
            Can&apos;t find the information you&apos;re after? Browse our Qantas Hotels FAQs for more help.
          </Text>
        </Container>
      </Box>
      <Container py={[8, 10]}>
        <Text pl="0" mb={[2, 0]} fontSize="lg" fontWeight="bold">
          Popular topics
        </Text>
        <FaqNavBar />
      </Container>
      <Box bg="white">
        <Container py={[4, 8]} px={[4, 8, 0]} isLast bg="white">
          <Box width={['100%', '100%', '70%']}>
            <Topic>
              <TitleText isFirst id="bookings">
                Booking Questions
              </TitleText>

              <QuestionWrapper>
                <QuestionText>Can I make a hotel booking via Qantas Hotels Customer Support line?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Hotel bookings can be made online through <QHHomePageLink text="our website" questionGroup="Booking Questions" />. The
                    Qantas Hotels Customer Team can provide assistance with any permitted changes or cancellation to your booking via{' '}
                    <LiveChatLink questionGroup="Booking Questions" />.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>
                  How do I know my online hotel booking is confirmed and what information is displayed in the email?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    You will receive a Booking Confirmation email with your confirmed reservation details within 24 hours of making the
                    booking. If you have not yet received your confirmation, re-check your Spam or Junk folder. If you still haven’t
                    received your Booking Confirmation, please contact our <LiveChatLink questionGroup="Booking Questions" /> customer
                    support team.
                  </AnswerText>
                  <AnswerText>
                    Booking Confirmation contains property details, the room type you selected, any inclusions with the room selected and
                    arrival instructions. For further details about arrival instructions or airport shuttles please contact the property
                    directly.
                  </AnswerText>
                  <AnswerText>
                    Booking Confirmation will also include a Tax Invoice along with information relating to cancelling the reservation, use
                    of Qantas Points and Vouchers.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>How can I request another copy of my hotel Booking Confirmation email?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    To request a new copy of your Booking Confirmation, contact our <LiveChatLink questionGroup="Booking Questions" />{' '}
                    customer support team for assistance.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>Can I book multiple rooms at the same time?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    This function is currently unavailable. Our development team are working to provide this feature in the near future.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>What if I book the hotel room for another person but do not stay as a guest?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    The customer identified in the booking is referred to as the ‘lead guest’. It is essential that the lead guest is one of
                    the people staying at the hotel as they will be asked to provide photo ID by the hotel at check-in. Importantly, the
                    person making payment for the booking does not need to be the same as the lead guest. To make changes to the lead guest,
                    contact our <LiveChatLink questionGroup="Booking Questions" /> customer support team.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>I need to arrange an airport shuttle/transfer for the hotel I booked?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>Please contact the hotel directly to arrange transfers.</AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>How do I make a special request for my online hotel booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Special requests can be added in the &quot;Request (Optional)&quot; section of the checkout page. Please note, special
                    requests cannot be guaranteed, are at the discretion of the hotel and subject to availability on the day of check-in.
                    Additional charges may be applied by the hotel at its discretion. For any further confirmation on special requests
                    please contact the property directly.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText id="documentation">What booking documentation do I need to check-in to the hotel?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    We recommend you take your Booking Confirmation email to assist at check-in. The lead guest will be required to present
                    photo ID at check-in, such as a drivers license, Government issued ID or Passport. Some hotels will require a credit
                    card for security deposit.
                  </AnswerText>
                  <AnswerText>
                    The hotel has the right to refuse any booking when the lead guest is unable to show sufficient photo ID if requested
                    prior to or at check-in, or where a minor is not accompanied by a parent/guardian.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>What are your policies regarding under 18s?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    You must be over 18 to make a booking on our website. Each hotel may have its own policy regarding guests under the age
                    of 18. However, in general, all guests under the age of 18 should be accompanied by a parent/guardian or an individual
                    over the age of 18.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>Can I make a hotel booking online if I live outside of Australia?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes. Please note, all transactions will be processed in Australian Dollars (AUD) and the Qantas Hotels website follows
                    AEDT/AEST.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
            <Topic>
              <TitleText id="QFF">Qantas Frequent Flyer Customers</TitleText>
              <QuestionWrapper>
                <QuestionText>
                  How long after making a Qantas hotel booking do I receive the allocated Qantas Frequent Flyer Points?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Qantas Points will automatically be awarded within 8 weeks of checkout if the name and Qantas Frequent Flyer number
                    provided at checkout match the details in your Qantas Frequent Flyer account.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>When will I earn the points for my Airbnb booking that I made through the Qantas website?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Qantas Frequent Flyer Points will automatically be awarded within 8 weeks of checkout. For more details about Qantas
                    Airbnb bookings, please check the <TermsAndConditionsLink questionGroup="QFF Customers" /> page.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>How many properties worldwide does Qantas Hotels have on offer where I can earn Qantas Points?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    There are currently more than 200,000 properties worldwide listed on qantas.com/hotels where Qantas Hotel customers can
                    book and earn Qantas Points.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>
                  At what rate will I earn Qantas Points for stays booked at{' '}
                  <QHHomePageLink fontSize="md" text="Qantas Hotels" questionGroup="QFF Customers" />?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>All bookings made through Qantas.com/hotels, will earn a minimum of 3 points per AU$1 spent.</AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>
                  Qantas Hotels often has Double Points or Triple Points promotional offers, will these be extended to all 200,000 plus
                  properties?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    This will depend on the promotional offer available at time of enquiry. Terms & Conditions for current promotional
                    offers are available online at qantas.com/hotels.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>I have a question regarding my Airbnb booking that was made through the Qantas hotels website.</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    For queries regarding Airbnb bookings through Qantas please visit the Airbnb{' '}
                    <TermsAndConditionsLink questionGroup="QFF Customers" /> page. You can also contact our{' '}
                    <LiveChatLink questionGroup="QFF Customers" /> customer support team for assistance.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
            <Topic>
              <TitleText id="payments">Payments and Changes</TitleText>
              <QuestionWrapper>
                <QuestionText>How can I pay for my Qantas Hotels booking and are there any fees associated with payment?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Qantas hotels accept the following payment methods:
                    <br /> - Visa (Credit and Debit)
                    <br /> - MasterCard (Credit and Debit)
                    <br /> - Amex.
                    <br />
                    We do not charge any booking fees.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>How do I know my credit card details will be kept secure for my Qantas Hotels booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    When you make payment for your hotel booking, a secure server is used to transmit your credit card details to our
                    financial institutions. Secure Socket Layer (SSL) ensures the security of all information you send through the website
                    via the payment page.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
            <Topic>
              <TitleText id="depositPay">Deposit Pay</TitleText>
              <QuestionWrapper>
                <QuestionText>What is Deposit Pay?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    An alternative way to pay for your hotel booking through Qantas Hotels, allowing you to book now with a minimum 20%
                    deposit, and pay the balance closer to the time of your stay. You can pay the minimum deposit in cash or use Qantas
                    Points, but the balance must be paid for in cash only.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>What is the minimum deposit I need to pay?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    The minimum deposit is 20% of the total transaction value. You can pay the minimum deposit in cash or use your Qantas
                    Points. If you use points, the minimum amount you can use is 5,000 points and you will need to provide a payment card to
                    secure the booking. If this is less than 20% of the total transaction value in points, you’ll need to add points points
                    or top up your deposit with cash.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>Can I pay more than the minimum deposit amount?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes, you can pay as much as you like up front, so long as the minimum amount you pay is 20% of the total transaction
                    value.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>Is Deposit Pay available on all Qantas Hotels properties?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Deposit Pay is available on selected domestic properties that offer a free cancellation window that is 21 days or more
                    in the future from the date of booking.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>How do I know which hotels have Deposit Pay available?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    You can filter hotel options to show those with Deposit Pay available by selecting ‘Pay a deposit up front’ under
                    ‘Payment options’ on the left hand side of the search page.{' '}
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>How long is the free cancellation window on hotels?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    This varies depending on the hotel, and can be anywhere from 30 days to 24 hours prior to arrival.{' '}
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>How do I make a Deposit Pay booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Once you have selected your stay and are proceeding with paying for your booking, select ‘Pay a deposit up front’ in
                    step 3 ‘Payment options’ and follow the steps to select the amount of deposit you wish to pay in cash or by using points
                    to secure your booking.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>Can I pay my deposit as a combination of Qantas Points and cash?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes, you can pay your deposit in cash, use points, a Qantas Hotels voucher, or a combination, as long as the total
                    deposit payment is 20% of the total transaction value.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>Can I pay my deposit with a Qantas Hotels voucher?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes, you can use a voucher, or a combination of a voucher, cash and points, as long as the total deposit payment is 20%
                    of the total transaction value, and you provide a payment card for the balance payment to be charged to when due.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>When do I have to pay the final cash balance of my booking ?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    The remaining balance will be charged 24 hours out from the end of the free cancellation window to the card details
                    provided at the time of booking. Please be sure to have sufficient funds on your card to avoid cancellation of your
                    booking. We will send you an email 7 days before the payment is due as a friendly reminder.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>How will I be charged the remaining cash balance?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    The balance will be automatically charged to the card details provided at the time of booking. If you would like to
                    update your card details, please contact <ContactUsLink text="Qantas Hotels Support Team" questionGroup="Deposit Pay" />{' '}
                    to assist you before your payment is due.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>Can I pay the final balance of my booking in points or using a voucher?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>No, all remaining balances need to be paid in cash only.</AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>Can I pay off the final balance of my booking earlier?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>No, not at the moment.</AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>How do I update my expired credit card?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Qantas Frequent Flyer members can visit the{' '}
                    <SelfServiceLink text="Self Service - manage your booking site" questionGroup="Deposit Pay" />, and click on the ‘update
                    expired credit card’ link to update. Alternatively, contact our{' '}
                    <ContactUsLink text="Qantas Hotels Support Team" questionGroup="Deposit Pay" /> for help.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>How do I view my Deposit Pay booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Visit the <SelfServiceLink text="Self Service - manage your booking site" questionGroup="Deposit Pay" />.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <SubTitleText>Cancellations and Amendments</SubTitleText>
              <QuestionWrapper>
                <QuestionText isFirst>Can I cancel my Deposit Pay booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>Yes, you can cancel your booking before the end of the free cancellation window.</AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>Is my deposit refundable?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes, the deposit is fully refundable, if you cancel your booking before the end of the free cancellation window.{' '}
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>How can I amend my Deposit Pay booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    If you want to change your Deposit Pay booking, you will need to cancel your booking (before the end of the free
                    cancellation window to be eligible for a refund of your deposit) and make a new booking. Please note, you cannot amend
                    your booking online via our{' '}
                    <SelfServiceLink text="Self Service - manage your booking site" questionGroup="Deposit Pay" />.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <QuestionWrapper>
                <QuestionText>If I cancel my Deposit Pay booking, can I get a credit voucher in lieu of a refund?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes, you will need to contact our <ContactUsLink text="Qantas Hotels Support Team" questionGroup="Deposit Pay" /> for
                    help.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
            <Topic>
              <SubTitleText>Assistance with your booking query</SubTitleText>
              <QuestionWrapper>
                <QuestionText isFirst>Who can I contact to help with any Deposit Pay booking queries?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Contact our <LiveChatLink questionGroup="Deposit Pay" /> customer support team or visit our{' '}
                    <ContactUsLink text="Contact us page" questionGroup="Deposit Pay" /> for assistance.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
              <SubTitleText>Qantas Holidays</SubTitleText>
              <QuestionWrapper>
                <QuestionText isFirst>Can I book with Qantas Holidays using Deposit Pay?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    You can book Luxury Offers with Deposit Pay, however Deposit Pay is not available for Qantas Holidays at the moment.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <TitleText id="modifications">Changes and Cancellations</TitleText>
              <QuestionWrapper>
                <QuestionText>
                  Can I cancel or change my reservation through the{' '}
                  <QHHomePageLink text="Qantas Hotels" questionGroup="Changes and Cancellations" /> website?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Cancel or change a Hotel reservation via the Qantas Hotels website for non refundable bookings. In order to change or
                    cancel a non refundable booking reservation contact our <LiveChatLink questionGroup="Changes and Cancellations" />{' '}
                    customer support team for assistance.
                  </AnswerText>
                  <AnswerText>
                    Customers can amend their start dates for bookings within an active free cancellation window using the Qantas Hotels -
                    self service/manage your booking portal. Refer below to see how you can access your booking information online, using
                    the Qantas Hotels self service portal.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText id="change-booking">How do I change a booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>If changes are permissible, our customer support team will be able to assist you.</AnswerText>
                  <AnswerText>
                    The following requests will be treated as a cancellation:
                    <br /> - Changing the property at which you plan to stay
                    <br /> - Changing both the date of arrival and departure for a booking, or
                    <br /> - Changing the room or offer booked.
                  </AnswerText>
                  <AnswerText>
                    Contact our <LiveChatLink questionGroup="Changes and Cancellations" /> customer support team for assistance.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText id="cancel-booking">Can I cancel my booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>If cancellation is permissible, our customer support team will be able to assist you.</AnswerText>
                  <AnswerText>
                    Contact our <LiveChatLink questionGroup="Changes and Cancellations" /> customer support team for assistance.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText id="cancellation-policy">What is a ‘free cancellation’ or ‘non-refundable’ cancellation?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>Every hotel sets its own cancellation policy for each room they sell.</AnswerText>
                  <AnswerText>
                    Free cancellation means you can cancel or change the reservation free of charge if done within the allocated time frame
                    set by the hotel. This information is displayed in the Booking Confirmation email.
                  </AnswerText>
                  <AnswerText>
                    A non-refundable policy means that fees or a fee will be charged to cancel or make changes to a reservation. This
                    information is displayed in the Booking Confirmation email.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>How do I access self service and my booking information?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    You can access your historical booking information by visiting the Qantas Hotels self service site via:
                  </AnswerText>
                  <AnswerText>
                    QFF members
                    <br />
                    - Login and click on the &apos;manage bookings&apos; link next to the login section
                    <br />- Access the ‘manage bookings’ link from your booking confirmation email.
                  </AnswerText>
                  <AnswerText>
                    Non QFF members <br />- Access the ‘manage bookings’ link from your booking confirmation email.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
            <Topic>
              <TitleText id="general">General Enquiries and Feedback</TitleText>
              <QuestionWrapper>
                <QuestionText id="feedback">What do I do if I have feedback?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    If you have feedback we would love to hear from you. To leave a comment or suggestion please complete our{' '}
                    <QHSupportLink text="customer feedback form" questionGroup="General Enquires" />.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>Who is Qantas Hotels?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Qantas Group Accommodation Pty Ltd is an associated entity of Qantas, and is a wholly owned subsidiary of Qantas Airways
                    Limited. Qantas Group Accommodation Pty Ltd provides access for customers to book accommodation through the Qantas
                    website.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>
                  What are the booking terms and conditions associated with Qantas Hotels (Qantas Group Accommodation Pty Ltd)?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    The conditions associated with your booking are displayed in your Booking Confirmation. General Booking Terms and
                    Conditions are also available online at our Booking <TermsAndConditionsLink questionGroup="General Enquires" /> page.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
            <Topic>
              <TitleText id="vouchers">Vouchers</TitleText>
              <QuestionWrapper>
                <QuestionText>What is the difference between a Hotel Voucher vs a Credit Voucher?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    A hotel Voucher (Promotion code) is a complimentary promotional or goodwill voucher provided by Qantas Hotels, for
                    example a Qantas Hotels Birthday Voucher worth $50, a $30 hotel Voucher due to booking a Qantas Airways flight or
                    receiving a goodwill voucher from the Contact Centre. This type of voucher will have a specific expiry date and can be
                    used on the payment page of Qantas Hotels or Qantas Holidays.
                  </AnswerText>
                  <AnswerText>
                    If a booking paid for in whole or in part with a Voucher is cancelled and you are entitled to a refund, the portion of
                    the booking paid for using the Voucher will not be refunded, either in cash or with a replacement Voucher.
                  </AnswerText>
                  <AnswerText>
                    For example if you used a $30 voucher and paid $400 using a credit card for a total booking value of $430 and you cancel
                    the booking, you will only receive $400 back (assuming the booking was fully refundable) either as a Credit Voucher or
                    refunded back to the original payment method used for the booking.
                  </AnswerText>
                  <AnswerText>
                    A Credit Voucher is provided due to a paid booking being refunded and a Credit Voucher being issued. Credit Vouchers are
                    valid for a period of 3 years from the date of issue. A Credit Voucher has more flexible terms of use than a hotel
                    Voucher. If you cancel a booking paid for in whole or part with a Credit Voucher, and you are entitled to a refund, you
                    will be issued with a replacement Credit Voucher for future use.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>How do I redeem a Qantas Hotels Voucher?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Hotel Vouchers can be redeemed through <QHHomePageLink text="our hotels website" questionGroup="Vouchers" /> and{' '}
                    <QHPackagesPageLink text="our Holidays website" questionGroup="Vouchers" />. After selecting a hotel or holiday offer
                    that you wish to book, enter the voucher code in the space provided on the Review and Pay page.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>Can I redeem Qantas Hotels Vouchers for products other than hotels?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes, Qantas Hotels Vouchers can be used on <QHHomePageLink text="Qantas hotel bookings" questionGroup="Vouchers" /> and
                    also <QHPackagesPageLink text="Qantas Holidays bookings" questionGroup="Vouchers" />, excluding Airbnb.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>Do Qantas Hotels Vouchers expire?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    Yes, each Voucher has a unique expiry date. The expiry date and terms of use are included in the Voucher email.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>What do I do if a Qantas Hotels Voucher code does not work?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    If your Voucher code is not accepted on the Review and Pay page, please contact our{' '}
                    <LiveChatLink questionGroup="Vouchers" /> customer support line for assistance.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>
                  If I cancel my Qantas Hotels or Qantas Holidays booking that I booked using a voucher, can I receive a refund in cash or
                  voucher?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    No, any amount paid using a Voucher is non-refundable. If you are entitled to a refund for the cancelled booking, and
                    the booking included another payment method in addition to the Voucher, any hotel cancellation fees will be deducted
                    from the amount paid by you, and you will receive a refund of the balance back to the payment card used for your
                    booking.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>Can I use my Qantas Hotels Voucher code on multiple bookings?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>Each Voucher code can only be used once on a single booking. Any unused amount will be forfeited.</AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
            <Topic isLast>
              <TitleText>Credit Vouchers</TitleText>
              <QuestionWrapper>
                <QuestionText>If I accept a credit voucher, how long do I have to use it?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    You will have 3 years from the date of issue to use the Credit Voucher with Qantas Hotels or Qantas Holidays.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>If I decide to take a credit voucher, what other benefits do I get?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    If your booking is refundable, you’ll earn a minimum 1,000 Bonus Qantas Points (table shown below) which will be added
                    to your account on the issue of the credit voucher. When you use the Credit Voucher on your next Qantas Hotels booking
                    you’ll also earn the standard points earn rate on the value of your booking.
                  </AnswerText>
                  <Table>
                    <Column>
                      <TableCell fontWeight="bold">Booking Value (AUD)</TableCell>
                      <TableCell>Up to $2,000</TableCell>
                      <TableCell>$2,001 and $5,000</TableCell>
                      <TableCell isLast>$5,001 and over</TableCell>
                    </Column>
                    <Column isRight>
                      <TableCell fontWeight="bold">Points awarded</TableCell>
                      <TableCell>1,000 bonus Qantas Points</TableCell>
                      <TableCell>2,000 bonus Qantas Points</TableCell>
                      <TableCell isLast>5,000 bonus Qantas Points</TableCell>
                    </Column>
                  </Table>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>What is a property specific credit note?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    If you had a booking that was non-refundable, we may have been able to negotiate with the hotel a Property Specific
                    Voucher, allowing you to retain the value of your booking for use with the specific hotel at a later date. The validity
                    period of a Property Specific Voucher will be determined by the relevant hotel, and included in the terms and conditions
                    of the Property Specific Voucher. Any changes or cancellations to a booking paid for using a Property Specific Voucher
                    are at the discretion of the relevant hotel and must be arranged by you with the hotel directly.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>What if your booking is non-refundable?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    If your booking is non-refundable and your circumstances change, you will need to discuss a refund with the relevant
                    hotel. In accordance with our Booking Conditions, the hotel’s cancellation policy applies to your booking. We may, if
                    requested by you, liaise with the hotel to have your booking refunded, but any refund (or voucher) in these
                    circumstances will be at the discretion of the hotel (unless you are otherwise entitled to a refund under the Australian
                    Consumer Guarantees).
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>
                  If I use the voucher on my next Qantas Hotels or Qantas Holidays booking but don’t use the whole amount, what happens to
                  the remaining balance?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    If you have a balance remaining on your Credit Voucher, you will automatically receive an email with a new voucher code
                    with the remaining balance shown. If the balance is below $50, we will contact you to arrange a refund.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>Can I use more than one voucher on the payment form to make a Booking?</QuestionText>
                <AnswerWrapper>
                  <AnswerText>At this point in time you can only use one Credit Voucher for each booking transaction.</AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>

              <QuestionWrapper>
                <QuestionText>
                  If I cancel my Qantas Hotels or Qantas Holidays booking that I booked using a credit voucher, can I receive a refund in
                  cash?
                </QuestionText>
                <AnswerWrapper>
                  <AnswerText>
                    No, any amount paid using a Credit Voucher is not redeemable for cash. If you are eligible for a refund, you will be
                    issued with a replacement Credit Voucher for the amount of your booking, less any booking cancellation fees if
                    applicable.
                  </AnswerText>
                </AnswerWrapper>
              </QuestionWrapper>
            </Topic>
          </Box>
        </Container>
      </Box>
    </Wrapper>
  );
};

export default FAQsLayout;
